import { CircularProgress, IconButton, Stack } from '@mui/material';
import { UserStatusEnum, AllUsersForUserManagementQuery } from '@/__generated__/graphql';
import { Block as BlockIcon } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState } from 'react';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem } from '@mui/material';
import { SocialConnections } from '@/types';

const SOCIAL_CONNECTIONS = new Set<string>(Object.values(SocialConnections));

type UserActionsProps = {
  loading: boolean;
  user: AllUsersForUserManagementQuery['allUsers'][0];
  isInvitationPending: boolean;
  handleOpenConfirmationDialog: (id: string) => void;
  handleResetPassword: (user: AllUsersForUserManagementQuery['allUsers'][0]) => void;
};

export const UserActions = ({
  loading,
  user,
  isInvitationPending,
  handleOpenConfirmationDialog,
  handleResetPassword,
}: UserActionsProps) => {
  const { user: currentUser } = useAuth0();
  const { id, status, connection, loginCount } = user;
  const isBlocked = status === UserStatusEnum.Blocked;
  const isSocialConnection = useMemo(() => {
    if (!connection) return;
    return SOCIAL_CONNECTIONS.has(connection);
  }, [connection]);

  const [anchorElement, setAnchorElement] = useState<undefined | HTMLElement>(undefined);
  const open = Boolean(anchorElement);

  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const actionsDisabled = useMemo(() => {
    return id === currentUser?.sub || isBlocked === true;
  }, [isBlocked, id, currentUser?.sub]);

  const handleAction = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    handleResetPassword(user);
    handleClose();
  };

  const handleDialog = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    handleOpenConfirmationDialog(id);
    handleClose();
  };

  const userLoginCount = useMemo(() => {
    return loginCount && loginCount > 0;
  }, [loginCount]);

  return (
    <Stack direction="row" sx={{ justifyContent: 'end', alignItems: 'center' }}>
      <IconButton
        aria-controls={open ? 'simple-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleIconClick}
        data-testid="menu-icon-button"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!isBlocked && (
          <MenuItem
            divider={isInvitationPending || !isSocialConnection}
            data-testid="block-user"
            disabled={actionsDisabled}
            onClick={handleDialog}
          >
            <BlockIcon sx={{ marginRight: 1 }} />
            Block User
          </MenuItem>
        )}
        {isBlocked && (
          <MenuItem onClick={handleDialog}>
            <LockOpenIcon sx={{ marginRight: 1 }} />
            Unblock User
          </MenuItem>
        )}
        {loading && <CircularProgress size={30} sx={{ ml: 2 }} />}
        {!loading && !isSocialConnection && !isBlocked && (
          <>
            <MenuItem
              data-testid="reset-password"
              onClick={handleAction}
              disabled={actionsDisabled && !userLoginCount}
            >
              <ForwardToInboxIcon sx={{ mr: 1 }} />
              {userLoginCount ? 'Reset Password' : 'Resend Invite'}
            </MenuItem>
          </>
        )}
      </Menu>
    </Stack>
  );
};
